import { Box, Stack } from '@mui/material'
import { IDonutChart } from 'models/ICattlesData'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import {
	defaultInitialState,
	setLamenessFilters,
} from 'store/lamenessFilterSlice'
import { useAppDispatch } from 'store/hooks'
import { COLORS } from 'styles/colors'
import { CardStandart } from 'ui/CardStandart'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographyPrimary16Bold } from 'ui/typography/TypographyPrimary16Bold'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { normalizeFatnessCategories } from 'utils/fatnessCategories'

type GroupHealthSummaryCardProps = {
	title: string
	items?: IDonutChart[]
	totalCattle: number
	totalAffected?: number | null
	groupId: number
	variant: 'lameness' | 'fatness'
	fillAvailableWidth?: boolean
}

const buildDonutBackground = (items: IDonutChart[]) => {
	const total = items.reduce((sum, item) => sum + item.value, 0)
	if (!total) return '#F1F1F1'

	let offset = 0
	const segments = items.map(item => {
		const start = offset
		offset += (item.value / total) * 100
		return `${item.color} ${start}% ${offset}%`
	})

	return `conic-gradient(${segments.join(', ')})`
}

const getLamenessScore = (label: string) => {
	const match = label.match(/\d+/)
	return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER
}

const getPreviousValue = (item: IDonutChart): number | null => {
	const itemWithHistory = item as IDonutChart & {
		prev_value?: number | null
		previous_value?: number | null
		prev?: number | null
		previous?: number | null
	}
	const rawPreviousValue =
		itemWithHistory.prev_value ??
		itemWithHistory.previous_value ??
		itemWithHistory.prev ??
		itemWithHistory.previous
	if (rawPreviousValue == null) return null

	const parsed = Number(rawPreviousValue)
	return Number.isFinite(parsed) ? parsed : null
}

const getDeltaArrow = (item: IDonutChart) => {
	const previousValue = getPreviousValue(item)
	if (previousValue == null) return null

	const delta = item.value - previousValue
	if (delta === 0) return null
	const isNormalFatness = item.label.trim().toLowerCase() === 'в норме'
	const isPositiveChange = isNormalFatness ? delta > 0 : delta < 0

	return {
		symbol: delta > 0 ? '↑' : '↓',
		color: isPositiveChange ? COLORS.success : COLORS.error,
	}
}

export const GroupHealthSummaryCard = ({
	title,
	items = [],
	totalCattle,
	totalAffected,
	groupId,
	variant,
	fillAvailableWidth = false,
}: GroupHealthSummaryCardProps) => {
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const sortedItems =
		variant === 'lameness'
			? [...items].sort(
					(a, b) => getLamenessScore(a.label) - getLamenessScore(b.label),
				)
			: normalizeFatnessCategories(items)
	const visibleItems = sortedItems.filter(item => item.value > 0)
	const total = visibleItems.reduce((sum, item) => sum + item.value, 0)

	const handleOpenList = () => {
		if (variant === 'lameness') {
			dispatch(
				setLamenessFilters({
					...defaultInitialState,
					groups: { ids: [groupId] },
				}),
			)
			navigate(`/cattles/lameness?group=${groupId}`)
			return
		}
		navigate(`/cattles/fatness?group=${groupId}`)
	}

	return (
		<CardStandart
			sx={{
				flex: fillAvailableWidth ? '1 1 0' : '0 1 360px',
				width: '100%',
				maxWidth: fillAvailableWidth ? 'none' : '360px',
				height: '340px',
				minWidth: 0,
				padding: '24px',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Stack direction='row' alignItems='center' gap='6px' mb='18px'>
				<TypographyPrimary16Bold>{title}</TypographyPrimary16Bold>
				<TypographySecondary>
					{moment().subtract(1, 'day').format('DD.MM.YY')}
				</TypographySecondary>
			</Stack>

			{variant === 'fatness' && (
				<Box display='flex' justifyContent='center' mb='16px'>
					<Box
						sx={{
							width: 112,
							height: 112,
							borderRadius: '50%',
							background: buildDonutBackground(visibleItems),
							position: 'relative',
							'&::after': {
								content: '""',
								position: 'absolute',
								inset: 25,
								borderRadius: '50%',
								backgroundColor: COLORS.white,
							},
						}}
					/>
				</Box>
			)}

			{variant === 'lameness' && (
				<>
					<Stack direction='row' justifyContent='space-between' mb='14px'>
						<TypographyPrimary>Всего хромых голов</TypographyPrimary>
						<TypographyPrimary16Bold>
							{totalAffected ?? 'Нет данных'}
						</TypographyPrimary16Bold>
					</Stack>
					<Box
						sx={{
							height: 8,
							borderRadius: '2px',
							overflow: 'hidden',
							display: 'flex',
							backgroundColor: COLORS.inputBorder,
							mb: '14px',
						}}
					>
						{visibleItems.map(item => (
							<Box
								key={item.label}
								sx={{
									height: '100%',
									backgroundColor: item.color,
									width:
										total > 0 ? `${(item.value / total) * 100}%` : undefined,
									flex: total > 0 ? undefined : 1,
								}}
							/>
						))}
					</Box>
				</>
			)}

			<Stack gap='8px'>
				<Stack
					direction='row'
					color={COLORS.secondary}
					sx={{
						borderBottom: `1px solid ${COLORS.inputBorder}`,
						paddingBottom: '6px',
						marginBottom: '2px',
					}}
				>
					<TypographySecondary sx={{ flex: 1 }}>
						{variant === 'lameness' ? 'Балл' : 'Категории'}
					</TypographySecondary>
					<TypographySecondary sx={{ width: 120, textAlign: 'right' }}>
						Количество голов
					</TypographySecondary>
					<TypographySecondary sx={{ width: 42, textAlign: 'right' }}>
						%
					</TypographySecondary>
				</Stack>
				{visibleItems.length ? (
					visibleItems.map(item => {
						const deltaArrow = getDeltaArrow(item)
						return (
							<Stack direction='row' alignItems='center' key={item.label}>
								<Stack
									direction='row'
									alignItems='center'
									gap='5px'
									sx={{ flex: 1 }}
								>
									<Box
										sx={{
											width: 7,
											height: 7,
											borderRadius: '50%',
											backgroundColor: item.color,
										}}
									/>
									<TypographyPrimary>{item.label}</TypographyPrimary>
								</Stack>
								<Stack
									direction='row'
									alignItems='center'
									justifyContent='flex-end'
									gap='6px'
									sx={{ width: 120 }}
								>
									<TypographyPrimary>{item.value}</TypographyPrimary>
									{deltaArrow && (
										<TypographyPrimary
											sx={{ color: deltaArrow.color, lineHeight: 1 }}
										>
											{deltaArrow.symbol}
										</TypographyPrimary>
									)}
								</Stack>
								<TypographyPrimary sx={{ width: 42, textAlign: 'right' }}>
									{totalCattle > 0
										? Math.round((item.value * 100) / totalCattle)
										: '—'}
								</TypographyPrimary>
							</Stack>
						)
					})
				) : (
					<TypographySecondary>Нет данных</TypographySecondary>
				)}
			</Stack>

			<Box
				component='button'
				onClick={handleOpenList}
				sx={{
					display: 'block',
					border: 0,
					background: 'transparent',
					color: COLORS.secondary,
					cursor: 'pointer',
					ml: 'auto',
					p: 0,
					mt: 'auto',
					pt: '22px',
				}}
			>
				Смотреть список →
			</Box>
		</CardStandart>
	)
}
